/**
 * Twisty — the control that opens a branch, and the one drawing of it.
 *
 * **Why it is a component and not two rules in two stylesheets.** It existed twice inside
 * `components/tree-table` — once for the table, once for the phone's card layout — and the
 * slicer rail was about to be the third (owner, 2026-09-10: *"the slicer should have the same
 * chevron for expand/collapse as the tree table"*). Retro rule 8: a cross-cutting thing lands
 * as one reusable object the first time it appears, never as an inline copy later domains
 * re-derive. The chevron, its two directions, its accessible name, the press that must not
 * become a selection, and the spacer a leaf keeps in its place are all one answer.
 *
 * **`ChevronRight` shut, `ChevronDown` open**, never a rotated single glyph: a rotation
 * animates through a diagonal that reads as neither state, and the two lucide paths are drawn
 * for the angles they are at.
 *
 * **The press is stopped from reaching the row.** The twisty is drawn *inside* the row it
 * belongs to — it has to be, or the indent could not push it — so without this the row's own
 * `mousedown` runs first and a branch somebody merely unfolded becomes the selection. Reported
 * by the owner on 2026-09-10 against the tree table; the same trap is waiting in every list
 * that puts a twisty in a pressable row, which is why the stopping lives here rather than at
 * each call site. Both `mousedown` and `click` are stopped, because the three lists act on
 * different events — the grid selects on `mousedown`, the picker chooses on `click` — and a
 * control that stopped only one would be safe in one list and wrong in the other.
 * `contextmenu` is deliberately *not* stopped: the twisty is part of the row, and a
 * right-click aimed at it means the row's menu.
 *
 * **`Spacer` is not decoration.** A leaf that draws nothing steps its name back in by the
 * twisty's width, so a branch's children and its childless siblings sit on two different
 * lefts and the eye reads a level that is not there.
 */

import { ChevronDown, ChevronRight } from "lucide-react";

import styles from "./Twisty.module.css";

/** `sm` is a table row's; `touch` is the 44px target the phone layout needs (brief 23). */
export type TwistySize = "sm" | "touch";

export interface TwistyProps {
  readonly open: boolean;
  /** Already translated, and already naming the row — *Expand Block A*, not *Expand*. */
  readonly label: string;
  readonly onToggle: () => void;
  readonly size?: TwistySize;
  /**
   * `false` inside a list whose *rows* are the tab stops, or whose whole box is one stop under
   * `aria-activedescendant` — the picker and the slicer rail. A twisty that kept its own stop
   * there would double the tab order of a two-thousand-row tree, and the pattern gives those
   * lists `ArrowRight` / `ArrowLeft` instead. Default `true`: the tree table's rows are not
   * tab stops, so its twisty has to be one.
   */
  readonly tabbable?: boolean;
}

export function Twisty({
  open,
  label,
  onToggle,
  size = "sm",
  tabbable = true,
}: TwistyProps) {
  const Chevron = open ? ChevronDown : ChevronRight;
  return (
    <button
      type="button"
      className={[styles.twisty, styles[size]].join(" ")}
      {...(tabbable ? {} : { tabIndex: -1 })}
      aria-expanded={open}
      aria-label={label}
      onMouseDown={(event) => {
        event.stopPropagation();
      }}
      // Two quick presses mean open-and-shut, not "edit the name" — the grid opens an editor
      // on a double click, and it must not hear this one.
      onDoubleClick={(event) => {
        event.stopPropagation();
      }}
      onClick={(event) => {
        // Both halves, because rows act on different events: the grid selects on `mousedown`
        // and the picker chooses on `click`, so stopping only one leaves the other list
        // treating an unfold as a choice.
        event.stopPropagation();
        onToggle();
      }}
    >
      <Chevron size={size === "touch" ? 16 : 14} strokeWidth={1.5} aria-hidden />
    </button>
  );
}

/** The width a leaf holds so that names line up down a branch. */
export function TwistySpacer({ size = "sm" }: { readonly size?: TwistySize }) {
  return (
    <span
      className={[styles.spacer, styles[size]].join(" ")}
      aria-hidden
    />
  );
}
